/**
 * Minimal typings + wrapper for the Web Speech API.
 *
 * `SpeechRecognition` is not in TypeScript's DOM lib, and it is still vendor
 * prefixed in Safari and Chrome, so the surface we use is declared here rather
 * than pulling in a polyfill. Recognition runs on-device/in-browser — no audio
 * is uploaded by this app.
 */

export interface SpeechResult {
  transcript: string;
  isFinal: boolean;
}

interface RecognitionAlternative {
  transcript: string;
}
interface RecognitionResult {
  readonly length: number;
  isFinal: boolean;
  [index: number]: RecognitionAlternative;
}
interface RecognitionEvent {
  resultIndex: number;
  results: { readonly length: number; [index: number]: RecognitionResult };
}
interface RecognitionErrorEvent {
  error: string;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

type RecognitionCtor = new () => SpeechRecognitionLike;

function getConstructor(): RecognitionCtor | undefined {
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

export const isSpeechSupported = (): boolean => Boolean(getConstructor());

export interface DictationHandle {
  stop(): void;
}

export interface DictationCallbacks {
  onResult(result: SpeechResult): void;
  onError(message: string): void;
  onEnd(): void;
}

const ERROR_MESSAGES: Record<string, string> = {
  'not-allowed': 'Microphone access was blocked. Allow it in your browser settings.',
  'service-not-allowed': 'Speech recognition is unavailable in this browser.',
  'no-speech': "Didn't catch that — try again.",
  network: 'Speech recognition needs a network connection.',
  aborted: '',
};

export function startDictation(callbacks: DictationCallbacks, lang = 'en-US'): DictationHandle | undefined {
  const Ctor = getConstructor();
  if (!Ctor) return undefined;

  const recognition = new Ctor();
  recognition.lang = lang;
  recognition.continuous = true;
  recognition.interimResults = true;

  recognition.onresult = (event) => {
    // Only the results added since the last event are new.
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      if (!result) continue;
      const alternative = result[0];
      if (!alternative) continue;
      callbacks.onResult({
        transcript: alternative.transcript,
        isFinal: result.isFinal,
      });
    }
  };

  recognition.onerror = (event) => {
    const message = ERROR_MESSAGES[event.error] ?? `Dictation error: ${event.error}`;
    if (message) callbacks.onError(message);
  };

  recognition.onend = () => callbacks.onEnd();

  recognition.start();
  return { stop: () => recognition.stop() };
}
